import {
    applyReduction,
    ProjectPickerRow,
    reducedSize
} from "./projectPickerRow";
import { ProjectReductionChange } from "./useProjectAssumptionsEditor";

export enum BulkProjectAction {
    Exclude = "exclude",
    Downsize = "downsize"
}

export const BULK_ACTION_INFO: Record<
    BulkProjectAction,
    {
        readonly label: string;
        /** Shown on the trigger while the action is being applied. */
        readonly activeLabel: string;
        readonly description: string;
    }
> = {
    [BulkProjectAction.Exclude]: {
        label: "Exclude projects",
        activeLabel: "Excluding projects",
        description: "Sets both reductions to 100%."
    },
    [BulkProjectAction.Downsize]: {
        label: "Downsize projects",
        activeLabel: "Downsizing projects",
        description: "Reduce one size or both by a percentage."
    }
};

export interface BulkActionRules {
    readonly isRowSelectable: (row: ProjectPickerRow) => boolean;
    readonly disabledReason: (row: ProjectPickerRow) => string;
}

const ALREADY_EXCLUDED_REASON = "Already excluded.";
const WHOLE_PROJECT_ONLY_REASON =
    "Studied in service or dropped, never derated.";

export const bulkActionRules = (
    action: BulkProjectAction,
    wholeProjectOnlyProjectIds: ReadonlySet<string>
): BulkActionRules => {
    switch (action) {
        case BulkProjectAction.Exclude:
            return {
                isRowSelectable: (row) => !row.isExcluded,
                disabledReason: () => ALREADY_EXCLUDED_REASON
            };
        case BulkProjectAction.Downsize:
            return {
                isRowSelectable: (row) =>
                    !row.isExcluded &&
                    !wholeProjectOnlyProjectIds.has(row.project.projectId),
                disabledReason: (row) =>
                    row.isExcluded
                        ? ALREADY_EXCLUDED_REASON
                        : WHOLE_PROJECT_ONLY_REASON
            };
    }
};

export type BulkDownsizeSide = "eris" | "nris" | "both";

export const priorQueueErisReason = (count: number): string =>
    `Prior-queue ERIS can only be reduced by 100% (${count} selected). Set 100%, apply to NRIS only, or deselect.`;

export const bulkDownsizeChanges = ({
    rows,
    capacityOnlyProjectIds,
    percent,
    applyTo
}: {
    readonly rows: ReadonlyArray<ProjectPickerRow>;
    readonly capacityOnlyProjectIds: ReadonlySet<string>;
    readonly percent: number;
    readonly applyTo: BulkDownsizeSide;
}): {
    readonly changes: ReadonlyArray<ProjectReductionChange>;
    readonly mwDelta: number;
    readonly disabledReason: string | undefined;
} => {
    const applyEris = applyTo !== "nris";
    const applyNris = applyTo !== "eris";
    // PQ energy is withdraw-only, so a partial ERIS never applies to any of them.
    const priorQueueCount = rows.filter((row) =>
        capacityOnlyProjectIds.has(row.project.projectId)
    ).length;
    if (applyEris && percent > 0 && percent < 100 && priorQueueCount > 0) {
        return {
            changes: [],
            mwDelta: 0,
            disabledReason: priorQueueErisReason(priorQueueCount)
        };
    }

    let mwDelta = 0;
    const changes = rows.map((row) => {
        const { projectId, type, size } = row.project;
        const current = reducedSize(row);
        if (applyEris) {
            mwDelta += current.erisMw - applyReduction(size.erisMw, percent);
        }
        if (applyNris) {
            mwDelta += current.nrisMw - applyReduction(size.nrisMw, percent);
        }
        return {
            projectId,
            projectType: type,
            ...(applyEris ? { eris: percent } : {}),
            ...(applyNris ? { nris: percent } : {})
        };
    });
    return { changes, mwDelta, disabledReason: undefined };
};
